import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Palette, Plus, Loader2, Wand2, Copy, Archive, History, Link2, ShieldAlert, Save, Sparkles, Trash2, AlertTriangle,
} from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";
const EMPTY = {
  name: "", description: "", source_type: "manual", reference_urls: [], style_prompt: "",
  hook_pattern: "", pacing: "balanced", narrative_structure: "", visual_approach: "",
  caption_style: "", music_mood: "", target_duration_min: 0, target_duration_max: 0,
  example_titles: [], channel_ids: [],
};

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-slate-300 text-sm mb-1 block">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-slate-600 mt-1">{hint}</p>}
    </div>
  );
}

function StyleForm({ initial, onSaved, onClose }) {
  const [f, setF] = useState({ ...EMPTY, ...(initial?.data || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required."); return; }
    setBusy(true);
    try {
      const body = { ...f, reference_urls: (typeof f.reference_urls === "string" ? f.reference_urls.split("\n") : f.reference_urls).map((x) => x.trim()).filter(Boolean), example_titles: typeof f.example_titles === "string" ? f.example_titles.split("\n").map((x) => x.trim()).filter(Boolean) : f.example_titles };
      if (initial?.id) await api.put(`/styles/${initial.id}`, body);
      else await api.post("/styles", body);
      toast.success(initial?.id ? "Style updated (new version)." : "Style created.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const refStr = Array.isArray(f.reference_urls) ? f.reference_urls.join("\n") : f.reference_urls;
  return (
    <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
      <Field label="Name"><Input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="style-name" /></Field>
      <Field label="Description"><Input className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="style-description" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Source type">
          <Select value={f.source_type} onValueChange={(v) => set("source_type", v)}>
            <SelectTrigger className={inputCls} data-testid="style-source"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="teacher-prompt">Teacher prompt</SelectItem>
              <SelectItem value="competitor-video">Competitor video</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Pacing">
          <Select value={f.pacing} onValueChange={(v) => set("pacing", v)}>
            <SelectTrigger className={inputCls} data-testid="style-pacing"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              <SelectItem value="fast">Fast</SelectItem><SelectItem value="balanced">Balanced</SelectItem><SelectItem value="slow">Slow</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Style prompt (core instruction)"><Textarea className={`${inputCls} min-h-[70px]`} value={f.style_prompt} onChange={(e) => set("style_prompt", e.target.value)} data-testid="style-prompt" /></Field>
      <Field label="Hook pattern"><Input className={inputCls} value={f.hook_pattern} onChange={(e) => set("hook_pattern", e.target.value)} placeholder="e.g. open with a bold question" data-testid="style-hook" /></Field>
      <Field label="Narrative structure"><Input className={inputCls} value={f.narrative_structure} onChange={(e) => set("narrative_structure", e.target.value)} placeholder="problem→solution / listicle / story-arc" data-testid="style-structure" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Visual approach"><Input className={inputCls} value={f.visual_approach} onChange={(e) => set("visual_approach", e.target.value)} /></Field>
        <Field label="Caption style"><Input className={inputCls} value={f.caption_style} onChange={(e) => set("caption_style", e.target.value)} placeholder="short-punchy / detailed / none" /></Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Music mood"><Input className={inputCls} value={f.music_mood} onChange={(e) => set("music_mood", e.target.value)} /></Field>
        <Field label="Min dur (s)"><Input type="number" className={inputCls} value={f.target_duration_min} onChange={(e) => set("target_duration_min", Number(e.target.value))} /></Field>
        <Field label="Max dur (s)"><Input type="number" className={inputCls} value={f.target_duration_max} onChange={(e) => set("target_duration_max", Number(e.target.value))} /></Field>
      </div>
      <Field label="Reference URLs (one per line)" hint="Links are saved but NOT reviewed. Only pasted transcripts/notes count as reviewed evidence.">
        <Textarea className={`${inputCls} font-mono text-xs min-h-[52px]`} value={refStr} onChange={(e) => set("reference_urls", e.target.value)} data-testid="style-refs" />
      </Field>
      <DialogFooter>
        <Button onClick={onClose} variant="ghost" className="text-slate-400 hover:bg-slate-800">Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="style-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> {initial?.id ? "Save new version" : "Create style"}</>}</Button>
      </DialogFooter>
    </div>
  );
}

function ExtractDialog({ onDraft }) {
  const [open, setOpen] = useState(false);
  const [sourceType, setSourceType] = useState("teacher-prompt");
  const [teacher, setTeacher] = useState("");
  const [transcript, setTranscript] = useState("");
  const [urls, setUrls] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const run = async () => {
    setBusy(true); setRefusal(null);
    try {
      const { data } = await api.post("/styles/extract", {
        source_type: sourceType, teacher_prompt: teacher, transcript,
        reference_urls: urls.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      if (!data.ok) { setRefusal(data.refusal); return; }
      toast.success("Style draft extracted — review & save.");
      setOpen(false); onDraft({ ...data.draft, reference_urls: urls.split("\n").map((x) => x.trim()).filter(Boolean) });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="style-extract-open"><Wand2 className="w-4 h-4 mr-1.5" /> Extract from reference</Button></DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader><DialogTitle>Extract style from reference</DialogTitle>
          <DialogDescription className="text-slate-500">Paste a teacher's prompt or the video's transcript/notes. I can't watch videos — a link alone isn't enough.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Source type">
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger className={inputCls} data-testid="extract-source"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                <SelectItem value="teacher-prompt">Teacher prompt</SelectItem>
                <SelectItem value="competitor-video">Competitor video</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Teacher prompt / guidance"><Textarea className={`${inputCls} min-h-[60px]`} value={teacher} onChange={(e) => setTeacher(e.target.value)} data-testid="extract-teacher" /></Field>
          <Field label="Transcript / your notes (reviewed evidence)"><Textarea className={`${inputCls} min-h-[60px]`} value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="extract-transcript" /></Field>
          <Field label="Reference URLs (links only — not reviewed)"><Textarea className={`${inputCls} font-mono text-xs min-h-[40px]`} value={urls} onChange={(e) => setUrls(e.target.value)} data-testid="extract-urls" /></Field>
          {refusal && (
            <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="extract-refusal">
              <ShieldAlert className="w-4 h-4 text-amber-300 mt-0.5 flex-none" /><p className="text-xs text-amber-200">{refusal}</p>
            </div>
          )}
          <Button onClick={run} disabled={busy} className="w-full bg-violet-600 hover:bg-violet-500 text-white" data-testid="extract-run">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Extract (paid)</>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Styles() {
  const [styles, setStyles] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try { const { data } = await api.get("/styles"); setStyles(data); } catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dup = async (id) => { try { await api.post(`/styles/${id}/duplicate`); toast.success("Duplicated."); load(); } catch (e) { toast.error(apiError(e)); } };
  const arch = async (id) => { try { await api.post(`/styles/${id}/archive`, { archived: true }); toast.success("Archived."); load(); } catch (e) { toast.error(apiError(e)); } };
  const openDelete = (s) => { setDelTarget(s); setDelConfirm(""); };
  const doDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/styles/${delTarget.id}`, { data: { confirm_name: delConfirm } });
      toast.success(`Style "${data.name}" deleted (${data.deleted?.style_versions || 0} version${data.deleted?.style_versions === 1 ? "" : "s"} removed)`);
      setDelTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeleting(false); }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!styles) return <LoadingState label="Loading styles…" />;

  const openCreate = (d = null) => { setDraft(d); setCreating(true); };

  return (
    <div className="animate-in-up">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1 flex items-center gap-1.5"><Palette className="w-3.5 h-3.5" /> Library</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Styles</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">Reusable video styles from competitor videos or teacher prompts. Pin a style version onto a project or an autopilot run — it shapes hook, pacing &amp; structure, never the content language.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExtractDialog onDraft={(d) => openCreate(d)} />
          <Button onClick={() => openCreate(null)} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="style-add"><Plus className="w-4 h-4 mr-1.5" /> New style</Button>
        </div>
      </div>

      {styles.length === 0 ? (
        <EmptyState icon={Palette} title="No styles yet" description="Create one manually or extract it from a teacher prompt / transcript." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="styles-list">
          {styles.map((s) => (
            <div key={s.id} className="bg-[#111827] border border-slate-800/80 rounded-xl p-4" data-testid={`style-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-slate-100">{s.data.name}</h3>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">v{s.version}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">{s.data.pacing}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{s.data.source_type}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{s.data.description || s.data.style_prompt?.slice(0, 90)}</p>
                  {s.data.hook_pattern && <p className="text-[11px] text-slate-400 mt-1"><span className="text-slate-600">hook:</span> {s.data.hook_pattern}</p>}
                  {(s.data.reference_urls || []).length > 0 && <p className="text-[11px] text-slate-600 mt-1 flex items-center gap-1"><Link2 className="w-3 h-3" /> {s.data.reference_urls.length} link(s) saved — not reviewed</p>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-3">
                <Button size="sm" onClick={() => setEditing(s)} className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid={`style-edit-${s.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => dup(s.id)} className="text-slate-300 hover:bg-slate-800" data-testid={`style-dup-${s.id}`}><Copy className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => arch(s.id)} className="text-rose-400 hover:bg-slate-800" data-testid={`style-arch-${s.id}`}><Archive className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => openDelete(s)} className="text-red-500 hover:bg-red-500/10 hover:text-red-400" data-testid={`style-delete-${s.id}`} title="Delete style"><Trash2 className="w-4 h-4" /></Button>
                <span className="ml-auto text-[10px] text-slate-600 flex items-center gap-1"><History className="w-3 h-3" /> {s.created_via}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle>New style</DialogTitle></DialogHeader>
          <StyleForm initial={draft ? { data: draft } : null} onSaved={() => { setCreating(false); load(); }} onClose={() => setCreating(false)} />
        </DialogContent>
      </Dialog>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle>Edit style (creates a new version)</DialogTitle></DialogHeader>
          {editing && <StyleForm initial={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="bg-[#111827] border-red-500/30 text-slate-100" data-testid="delete-style-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300"><AlertTriangle className="w-5 h-5" /> Delete style permanently</DialogTitle>
            <DialogDescription className="text-slate-400">
              This permanently deletes <span className="font-semibold text-slate-200">{delTarget?.data?.name}</span> and all its saved versions. <span className="text-red-300 font-medium">This cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <Label className="text-slate-300 text-sm mb-1.5 block">Type <span className="font-mono text-slate-100">{delTarget?.data?.name}</span> to confirm</Label>
            <Input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} autoFocus placeholder={delTarget?.data?.name} data-testid="delete-style-confirm-input" className="bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-red-500/40" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="cancel-delete-style">Cancel</Button>
            <Button onClick={doDelete} disabled={deleting || delConfirm !== delTarget?.data?.name} className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-40" data-testid="confirm-delete-style">
              {deleting ? "Deleting…" : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
